import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Narration for the guided tutorial.
 *
 * Clips are pre-rendered by scripts/generateTutorialAudio.js and served as
 * static files, so a step's line starts the moment it opens. Everything here
 * degrades quietly: if the manifest is missing — nobody has run the generator
 * yet — the tutorial simply runs silent and no controls are offered.
 */

const BASE = '/assets/tutorial-audio';
const STORAGE_KEY = 'embroiderytrace.tutorialAudio';

const readPreference = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
};

const useTutorialAudio = ({ tutorialId, stepId, pageIndex, active }) => {
  const [manifest, setManifest] = useState(null);
  const [enabled, setEnabled] = useState(readPreference);

  const narrationRef = useRef(null);

  // Load the manifest once. A 404 just means no audio has been generated.
  useEffect(() => {
    let cancelled = false;

    fetch(`${BASE}/manifest.json`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!cancelled) setManifest(data);
      })
      .catch(() => {
        if (!cancelled) setManifest(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const available = !!manifest;

  const stopNarration = useCallback(() => {
    const narration = narrationRef.current;
    if (!narration) return;

    narration.pause();
    narration.currentTime = 0;
    narrationRef.current = null;
  }, []);

  // Narration follows whichever page of dialogue is on screen.
  useEffect(() => {
    if (!active || !enabled || !manifest || !stepId) return undefined;

    const clips = manifest.tutorials?.[tutorialId]?.[stepId];
    const file = clips?.[pageIndex];
    if (!file) return undefined;

    stopNarration();

    const narration = new Audio(`${BASE}/voice/${file}`);
    narrationRef.current = narration;

    // Autoplay can still be refused if the browser saw no gesture; that's not
    // worth surfacing, the tutorial is perfectly usable without it.
    narration.play().catch(() => {});

    return () => {
      narration.pause();
    };
  }, [active, enabled, manifest, tutorialId, stepId, pageIndex, stopNarration]);

  // Silence everything the moment the tutorial closes.
  useEffect(() => {
    if (active) return;
    stopNarration();
  }, [active, stopNarration]);

  const toggle = useCallback(() => {
    setEnabled((previous) => {
      const next = !previous;

      try {
        window.localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off');
      } catch {
        // A blocked storage API shouldn't stop the toggle working this session.
      }

      if (!next) stopNarration();
      return next;
    });
  }, [stopNarration]);

  return { available, enabled, toggle };
};

export default useTutorialAudio;
